// Step 1, Dec page, on the Record (spec C7.4, C8, C11, C16), with the markup and words of the Record
// board's fetch states: Fetch from [Carrier] with Open [Carrier] site, the progress line with the
// code countdown, waiting behind another record, No code received with Try again, Fetch failed
// twice, Login rejected, Not available yet, and the fetched files with View. A fetch keeps going on
// the server when the person leaves the page; the step asks for news every 2 seconds while it is open.
// The screenshot of the last page the bot saw is the Admin's only (Yehuda, Oct 25).
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import type { Api, FetchView, ReadingFixField, RenewalDetail, UploadKind } from '../auth/api';
import { Toast } from '../shell/Toast';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Viewer, type ViewerFile } from '../shell/Viewer';
import { fetchStartedEvent } from '../shell/FetchNotices';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckIcon,
  ChevronRightIcon,
  ClockIcon,
  ExternalLinkIcon,
  FileIcon,
  InfoIcon,
  RefreshIcon,
  TrashIcon,
  UploadIcon,
} from '../shell/icons';
import { Tip } from '../shell/Tip';
import { FoldChevron } from './StepFold';
import { LastTermFold } from './LastTermFold';
import { isAutoLine } from './renewal-lines';
import { useStepFold } from './use-step-fold';
import {
  attemptLine,
  decDeleteOff,
  decDeleteRefusal,
  codeSecondsLeft,
  codeWaitSeconds,
  decStamp,
  failedOnceLine,
  failureReason,
  mmss,
  notOnSite,
  progressRows,
  restingLine,
  siteDownLine,
  notConfiguredLine,
  slowStartSeconds,
  splitDocs,
  waitingForCarrierLine,
  stampTime,
  startFailedText,
} from './fetch-lines';
import { closedLockText, previouslyStamp } from './step-lines';
import { UploadDialog } from './UploadDialog';
import { isFixField } from './fix-lines';
import {
  checksHeadline,
  checkText,
  otherInsured,
  readingLine,
  readingOpen,
  readingsPending,
} from './reading-lines';
import { pickProblem, uploadedText } from './upload-lines';

const pollMs = 2000;

type Props = {
  api: Api;
  renewal: RenewalDetail;
  initial: FetchView;
  isAdmin: boolean;
  /** Told whenever the step's view changes, so the Record's other cards follow (the premium). */
  onView?: (view: FetchView) => void;
  /** A newer view from elsewhere on the Record (a value confirmed or corrected): shown at once. */
  pushed?: { at: number; view: FetchView } | null;
  /** Check on a value to confirm: the Record opens the confirm or correct dialog (spec C10). */
  onCheck?: (field: ReadingFixField) => void;
  /** Step 1 marked done previously (S16), from the steps' view: who and the day. */
  previously?: { by: string | null; date: string } | null;
  /** Delete is offered: no step 2 to 6 is ticked yet (S17). */
  deletable?: boolean;
};

export function DecPageStep({
  api,
  renewal,
  initial,
  isAdmin,
  onView,
  pushed,
  onCheck,
  previously = null,
  deletable = false,
}: Props) {
  const decDeletable = deletable;
  const [view, setView] = useState(initial);
  // A view pushed from the Record replaces this one once (React's "adjust state on a prop change").
  const [seenPush, setSeenPush] = useState(pushed?.at ?? 0);
  if (pushed && pushed.at !== seenPush) {
    setSeenPush(pushed.at);
    setView(pushed.view);
  }
  const [toast, setToast] = useState<{ id: number; text: string; undoDec?: string } | null>(null);
  // Step 1's Delete of a dec page (S17): the confirm, then the toast's Undo.
  const [deleting, setDeleting] = useState<{ id: string; fileName: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  async function removeDec(docId: string) {
    setRemoving(true);
    const res = await api.deleteDecPage(renewal.id, docId);
    setRemoving(false);
    setDeleting(null);
    if (res.ok) {
      setView(res.body.fetch);
      setToast({ id: Date.now(), text: 'Dec page deleted.', undoDec: docId });
    } else setToast({ id: Date.now(), text: decDeleteRefusal(res.error) });
  }
  async function restoreDec(docId: string) {
    const res = await api.restoreDecPage(renewal.id, docId);
    if (res.ok) {
      setView(res.body.fetch);
      setToast({ id: Date.now(), text: 'Undone. The dec page is back.' });
    } else setToast({ id: Date.now(), text: decDeleteRefusal(res.error) });
  }
  const [file, setFile] = useState<ViewerFile | null>(null);
  const [starting, setStarting] = useState(false);
  const [details, setDetails] = useState(false);
  // Upload PDF: the hidden file picker, what the next pick is taken to be, and the picked file.
  const pickRef = useRef<HTMLInputElement>(null);
  const [pickAs, setPickAs] = useState<UploadKind>('dec_page');
  const [picked, setPicked] = useState<{ file: File; kind: UploadKind } | null>(null);
  // The server's clock, moved on by this page's own clock between answers.
  const [skew] = useState(() => Date.parse(initial.now) - Date.now());
  const [now, setNow] = useState(() => new Date(Date.now() + skew));

  const job = view.job;
  const open = job?.status === 'queued' || job?.status === 'running';

  useEffect(() => {
    onView?.(view);
  }, [onView, view]);

  // The carrier rests after its site was down: ask again when the wait is over, so Fetch opens.
  const restingUntil = view.carrierRestingUntil;
  useEffect(() => {
    if (!restingUntil) return;
    const ms = Math.max(1_000, Date.parse(restingUntil) - (Date.now() + skew) + 1_000);
    const timer = setTimeout(async () => {
      try {
        setView(await api.fetchView(renewal.id));
      } catch {
        // The next look at the page asks again.
      }
    }, ms);
    return () => clearTimeout(timer);
  }, [api, renewal.id, restingUntil, skew]);

  // A fetch going, or the dec page (or the prior term's) still being read: ask again every 2
  // seconds.
  const reading = readingsPending(view.reading);
  useEffect(() => {
    if (!open && !reading) return;
    const tick = setInterval(() => setNow(new Date(Date.now() + skew)), 1000);
    const poll = setInterval(async () => {
      try {
        setView(await api.fetchView(renewal.id));
      } catch {
        // A missed check is fine: the next one asks again.
      }
    }, pollMs);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [api, open, reading, renewal.id, skew]);

  async function start() {
    setStarting(true);
    const res = await api.startFetch(renewal.id);
    setStarting(false);
    if (res.ok) {
      setView(res.body.fetch);
      window.dispatchEvent(new Event(fetchStartedEvent));
      return;
    }
    const sent = res.body.fetch as FetchView | undefined;
    if (sent) setView(sent);
    else setToast({ id: Date.now(), text: startFailedText });
  }

  function pick(kind: UploadKind) {
    setPickAs(kind);
    pickRef.current?.click();
  }

  function onPicked(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    // Cleared, so picking the same file again still opens the dialog.
    e.target.value = '';
    if (!f) return;
    const problem = pickProblem(f);
    if (problem) setToast({ id: Date.now(), text: problem });
    else setPicked({ file: f, kind: pickAs });
  }

  const carrier = view.carrier;
  const name = carrier?.name ?? renewal.carrier ?? 'the carrier';
  const dec = view.documents.find((d) => d.kind === 'dec_page') ?? null;
  // Auto: the ID cards belong to step 1 too (Rachy, Oct 28: the insured gets the policy and the ID
  // cards). Without them the step never looks complete, and its second row offers Upload PDF.
  const auto = isAutoLine(renewal.line);
  const cardsMissing = auto && !!dec && !view.documents.some((d) => d.kind === 'id_cards');
  // This term's files on top; last term's dec page in a closed list below (Yehuda, Oct 28).
  const files = splitDocs(view.documents);
  // Marked done previously with no dec page (S16): done, and Upload PDF still adds the dec page.
  const marked = !dec && !!previously;
  const complete = (!!dec && !cardsMissing) || marked;
  const fetchedByPortal = dec ? dec.source === 'fetch' : !!carrier;
  const why = marked
    ? 'Done before the portal. Upload PDF still adds the dec page.'
    : auto
      ? `Dec page and ID cards from ${name}${fetchedByPortal ? ', fetched by the portal' : ''}`
      : dec
        ? 'The renewal dec page is attached.'
        : 'Get the renewal dec page.';
  const subline = [renewal.insured, name, renewal.line, renewal.policyNumber].join(' | ');
  const viewDoc = (d: FetchView['documents'][number]) =>
    setFile({
      title: d.fileName,
      subline,
      src: api.documentUrl(d.id),
      downloadHref: api.documentUrl(d.id, true),
      kind: 'pdf',
    });
  const viewShot = (id: string, at: string) =>
    setFile({
      title: `Last page the bot saw, ${stampTime(at)}`,
      subline,
      src: api.documentUrl(id),
      downloadHref: api.documentUrl(id, true),
      kind: 'image',
    });

  // Upload PDF, for whoever works the record (spec C8): any carrier, and the way out of a fetch
  // that failed twice or a rejected login (spec C11).
  const uploadButton = (primary: boolean) =>
    view.canWork && (
      <button
        type="button"
        className={primary ? 'btn btn-primary' : 'btn'}
        aria-haspopup="dialog"
        onClick={() => pick('dec_page')}
      >
        <UploadIcon className="ico s14" />
        Upload PDF
      </button>
    );

  const siteButton = (primary: boolean) =>
    carrier && (
      <a
        className={primary ? 'btn btn-primary' : 'btn'}
        href={carrier.siteUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ExternalLinkIcon className="ico s14" />
        Open {carrier.name} site
      </a>
    );

  const lastFailure = view.failures[0] ?? null;
  const failedJob = job?.status === 'failed' ? job : null;
  // The fetch gave up (failed twice, or the login was rejected): the step's number turns red, so it
  // stands out (Yehuda, Oct 29). One failed attempt that will be tried again stays as it is.
  const failedOut = !!view.blocked;
  const neverStarted = !!failedJob && !failedJob.startedAt;
  // The carrier's site was down: not an attempt, so Fetch stays as it was.
  const siteDown = !!failedJob && failedJob.siteDown;
  // The worker here has no login or code mailbox for the carrier (staging): not an attempt either.
  const notConfigured = !!failedJob && failedJob.errorKind === 'not_configured';
  const queuedFor =
    job?.status === 'queued' ? (now.getTime() - Date.parse(job.createdAt)) / 1000 : 0;

  // Done (the dec page is attached) folds to one line, but stays open while the VA still has to
  // look: the PDF is being read, could not be read, or has values to check.
  const r = view.reading;
  const needsLook =
    !!r && (readingOpen(r) || r.status === 'failed' || r.checks.length > 0 || otherInsured(r));
  const fold = useStepFold(
    complete ? (needsLook ? 'look' : 'done') : dec ? 'cards' : 'now',
    !complete || needsLook,
  );
  // A closed renewal is read only (spec C16): a dec page step not finished by then is locked like
  // the steps after it, with nothing to press, until the Manager reopens it.
  if (view.closed && !complete) {
    return (
      <div className="step locked" aria-disabled="true" aria-label="Step 1, Dec page">
        <div className="step-h">
          <span className="num num-next">1</span>
          <span className="st-t">
            <span className="st-tr">
              <span className="st-title next">Dec page</span>
            </span>
            <span className="st-desc t12 muted">{closedLockText}</span>
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className={dec || marked ? 'step' : 'step now'}>
      <div className="step-h" onClick={fold.toggle}>
        <span
          className={
            complete
              ? 'num num-done'
              : dec
                ? 'num num-next'
                : failedOut
                  ? 'num num-fail'
                  : 'num num-now'
          }
        >
          {complete ? <CheckIcon className="ico s12" /> : 1}
        </span>
        <span className="st-t">
          <span className="st-tr">
            <span className={complete ? 'st-title' : 'st-title now'}>Dec page</span>
          </span>
          <span className="st-desc t12 muted">{why}</span>
        </span>
        {complete ? (
          <span className="stamp ok">
            {dec
              ? decStamp(dec, carrier?.name ?? null)
              : previously && previouslyStamp(previously.by, previously.date)}
          </span>
        ) : cardsMissing ? (
          <span className="stamp now">ID cards missing</span>
        ) : (
          <span className="stamp">Current step</span>
        )}
        <FoldChevron open={fold.open} onToggle={fold.toggle} title="Dec page" />
      </div>
      {fold.open && (
        <div className="step-b">
          {!carrier && (
            <div className="fsx grey">
              <div className="fsx-h">
                <InfoIcon />
                <span>
                  {name} has no auto fetch. Get the dec page from the carrier site, then use Upload
                  PDF.
                </span>
              </div>
              {view.canWork && <div className="row g8 wrap">{uploadButton(!dec)}</div>}
            </div>
          )}

          {/* Ready: Fetch from [Carrier], or Fetch again after a good fetch or Not available yet. */}
          {carrier &&
            !open &&
            !view.blocked &&
            (!failedJob || neverStarted || siteDown || notConfigured) && (
              <>
                {view.canWork && (
                  <div className="row g8 wrap">
                    <button
                      type="button"
                      className={dec ? 'btn' : 'btn btn-primary'}
                      disabled={!view.canFetch || starting}
                      onClick={start}
                    >
                      <RefreshIcon className="ico s14" />
                      {dec ? 'Fetch again' : `Fetch from ${carrier.name}`}
                    </button>
                    {siteButton(false)}
                    {uploadButton(false)}
                  </div>
                )}
                {notConfigured && (
                  <span className="okline amber">
                    <InfoIcon className="ico s14" />
                    <span>{notConfiguredLine(name)}</span>
                  </span>
                )}
                {!dec && !notConfigured && (
                  <p className="m0 t12 muted">
                    Logs in, retrieves the 2FA code, downloads the dec page. About 1 minute.
                  </p>
                )}
                {neverStarted && (
                  <span className="okline amber">
                    <ClockIcon className="ico s14" />
                    <span>The last fetch did not start. Press Fetch again.</span>
                  </span>
                )}
                {view.carrierRestingUntil ? (
                  <span className="okline amber">
                    <ClockIcon className="ico s14" />
                    <span>{restingLine(name, view.carrierRestingUntil, false)}</span>
                  </span>
                ) : (
                  siteDown &&
                  failedJob && (
                    <span className="okline amber">
                      <ClockIcon className="ico s14" />
                      <span>{siteDownLine(name, failedJob.finishedAt)}</span>
                    </span>
                  )
                )}
                {siteDown && failedJob && isAdmin && (
                  <div className="det2-b">
                    {failedJob.errorRef && (
                      <div className="att">
                        <span className="mono">
                          {stampTime(failedJob.finishedAt ?? failedJob.stageAt)}
                        </span>
                        <span>
                          {failureReason('site_down', name)} (reference {failedJob.errorRef})
                        </span>
                      </div>
                    )}
                    {failedJob.screenshotId && (
                      <ShotButton
                        shot={{
                          screenshotId: failedJob.screenshotId,
                          at: failedJob.finishedAt ?? failedJob.stageAt,
                        }}
                        host={view.carrier?.siteUrl}
                        onShot={viewShot}
                      />
                    )}
                  </div>
                )}
              </>
            )}

          {job?.status === 'ok' &&
            job.notAvailable &&
            !dec &&
            (() => {
              // Still early for this carrier: a plain note, not the amber warning.
              const n = notOnSite(name, view);
              return (
                <span className={n.expected ? 'row g6 t12 muted' : 'okline amber'}>
                  <ClockIcon className="ico s14" />
                  <span>{n.text}</span>
                </span>
              );
            })()}
          {job?.status === 'ok' && job.nonRenewal && (
            <span className="okline amber">
              <AlertTriangleIcon className="ico s14" />
              <span>{name} shows a non-renewal notice for this policy.</span>
            </span>
          )}

          {/* Waiting in line behind another record's fetch for the same carrier. */}
          {job?.status === 'queued' && (
            <div className="fsx muted">
              <div className="fsx-h">
                <span className="spin" />
                <span>
                  {view.carrierRestingUntil
                    ? restingLine(name, view.carrierRestingUntil, true)
                    : job.stage === 'waiting_for_carrier'
                      ? waitingForCarrierLine
                      : job.carrierBusy
                        ? `A fetch for ${name} is already running for another record. Yours starts next.`
                        : queuedFor > slowStartSeconds
                          ? `Not started yet. If it does not start in a few minutes, use Open ${name} site.`
                          : `Starting the fetch from ${name}.`}
                </span>
              </div>
              {job.carrierBusy && job.queuePosition !== null && (
                <div className="row g12">
                  <span className="tag t-grey">Position {job.queuePosition} in line</span>
                </div>
              )}
            </div>
          )}

          {/* Running: the progress line (spec C11). */}
          {job?.status === 'running' && (
            <div className="fsbox">
              <div className="row between">
                <span className="t13 w6 ink">Fetching from {name}</span>
                <span className="mono muted">
                  {mmss((now.getTime() - Date.parse(job.startedAt ?? job.createdAt)) / 1000)}
                </span>
              </div>
              <ol className="fslist">
                {progressRows(job.stage).map((r) => (
                  <li key={r.label} className={`fsrow ${r.cls}`.trim()}>
                    <span className="xdot">
                      {r.cls === 'ok' && <CheckIcon className="ico s12" />}
                      {r.cls === 'now' && <span className="spin" />}
                    </span>
                    <span>{r.label}</span>
                  </li>
                ))}
              </ol>
              {job.stage === 'waiting_for_code' && (
                <CodeCountdown left={codeSecondsLeft(job.stageAt, now)} />
              )}
              <p className="m0 t12 muted">
                You can leave this page. A message shows when it finishes.
              </p>
            </div>
          )}

          {/* One failed attempt: Try again (spec C11). */}
          {failedJob && !neverStarted && !siteDown && !notConfigured && !view.blocked && (
            <div className="fsx amber">
              <div className="fsx-h">
                <AlertTriangleIcon />
                <span>{failedOnceLine(failedJob.errorKind, name, true)}</span>
              </div>
              {view.canWork && (
                <div className="row g8 wrap">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!view.canFetch || starting}
                    onClick={start}
                  >
                    <RefreshIcon className="ico s14" />
                    Try again
                  </button>
                  {siteButton(false)}
                  {uploadButton(false)}
                </div>
              )}
              {view.carrierRestingUntil && (
                <span className="okline amber">
                  <ClockIcon className="ico s14" />
                  <span>{restingLine(name, view.carrierRestingUntil, false)}</span>
                </span>
              )}
              <p className="m0 t12 muted">{attemptLine(view.failures.length, 2)}</p>
              {isAdmin && lastFailure && (
                <AdminFailures view={view} carrier={name} onShot={viewShot} />
              )}
            </div>
          )}

          {view.blocked === 'login_rejected' && (
            <div className="fsx red">
              <div className="fsx-h">
                <AlertCircleIcon />
                <span>{name} rejected the login. The password may have changed.</span>
              </div>
              <div className="row g8 wrap">
                {siteButton(true)}
                {uploadButton(false)}
              </div>
              <p className="m0 t12 muted">
                This record shows in Renewals under Needs attention: Login rejected. Fetch opens
                again once a {name} fetch works.
              </p>
              {isAdmin && <AdminFailures view={view} carrier={name} onShot={viewShot} />}
            </div>
          )}

          {view.blocked === 'failed_twice' && (
            <div className="fsx red">
              <div className="fsx-h">
                <AlertCircleIcon />
                <span>Fetch failed twice. Get the dec page from the {name} site.</span>
              </div>
              {lastFailure && (
                <p className="m0 t13 text2">Reason: {failureReason(lastFailure.errorKind, name)}</p>
              )}
              <div className="row g8 wrap">
                {siteButton(true)}
                {uploadButton(false)}
              </div>
              <div className="det2">
                <button
                  type="button"
                  className="det2-h"
                  aria-expanded={details}
                  onClick={() => setDetails((d) => !d)}
                >
                  <ChevronRightIcon className="ico s12" />
                  Details
                </button>
                {details && (
                  <div className="det2-b">
                    {[...view.failures].reverse().map((f, i) => (
                      <div key={f.jobId} className="att">
                        <span className="mono">{stampTime(f.at)}</span>
                        <span>
                          Attempt {i + 1}: {failureReason(f.errorKind, name)}
                          {f.errorRef && ` (reference ${f.errorRef})`}
                        </span>
                      </div>
                    ))}
                    {isAdmin && <AdminShot view={view} onShot={viewShot} />}
                  </div>
                )}
              </div>
              <p className="m0 t12 muted">
                This record shows in Renewals under Needs attention: Fetch failed twice.
              </p>
            </div>
          )}

          {view.documents.length > 0 && (
            <div className="okbox">
              {files.current.map((d) => (
                <div key={d.id} className="frow">
                  <span className="file">
                    <FileIcon className="ico s14" />
                    {d.fileName}
                  </span>
                  <span className="ml-auto row g6">
                    <button
                      type="button"
                      className="btn btn-sm"
                      aria-haspopup="dialog"
                      onClick={() => viewDoc(d)}
                    >
                      View
                    </button>
                    {d.kind === 'dec_page' && view.canWork && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm"
                          aria-haspopup="dialog"
                          onClick={() => pick('dec_page')}
                        >
                          Replace
                        </button>
                        {/* Delete (S17): while steps 2 to 6 are untouched; after that Replace. */}
                        {decDeletable ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-del"
                            onClick={() => setDeleting({ id: d.id, fileName: d.fileName })}
                          >
                            <TrashIcon className="ico s12" />
                            Delete
                          </button>
                        ) : (
                          <Tip text={decDeleteOff} focusable>
                            <button type="button" className="btn btn-sm btn-del" disabled>
                              <TrashIcon className="ico s12" />
                              Delete
                            </button>
                          </Tip>
                        )}
                      </>
                    )}
                  </span>
                </div>
              ))}
              {cardsMissing && (
                <div className="frow">
                  <span className="okline amber">
                    <AlertTriangleIcon className="ico s14" />
                    <span>ID cards are not on the record yet</span>
                  </span>
                  {view.canWork && (
                    <span className="ml-auto row g6">
                      <button
                        type="button"
                        className="btn btn-sm"
                        aria-haspopup="dialog"
                        onClick={() => pick('id_cards')}
                      >
                        <UploadIcon className="ico s12" />
                        Upload PDF
                      </button>
                    </span>
                  )}
                </div>
              )}
              {dec && <span className="t12 muted">{decStamp(dec, carrier?.name ?? null)}</span>}
              {dec && view.reading && <ReadingBlock reading={view.reading} onCheck={onCheck} />}
              {files.lastTerm.length > 0 && (
                <LastTermFold>
                  {files.lastTerm.map((d) => (
                    <div key={d.id} className="frow">
                      <span className="file">
                        <FileIcon className="ico s14" />
                        {d.fileName}
                      </span>
                      <span className="ml-auto row g6">
                        <button
                          type="button"
                          className="btn btn-sm"
                          aria-haspopup="dialog"
                          onClick={() => viewDoc(d)}
                        >
                          View
                        </button>
                      </span>
                    </div>
                  ))}
                </LastTermFold>
              )}
            </div>
          )}
        </div>
      )}
      <input
        ref={pickRef}
        type="file"
        accept="application/pdf,.pdf"
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={onPicked}
      />
      {picked && (
        <UploadDialog
          api={api}
          renewalId={renewal.id}
          file={picked.file}
          kind={picked.kind}
          onClose={() => setPicked(null)}
          onUploaded={(result, kind) => {
            setPicked(null);
            setView(result.fetch);
            setToast({ id: Date.now(), text: uploadedText(kind, result.duplicate) });
          }}
        />
      )}
      {file && <Viewer {...file} onClose={() => setFile(null)} />}
      {deleting && (
        <ConfirmPopup
          title="Delete the dec page?"
          line={`${deleting.fileName} leaves step 1 and the steps read the record again. It stays in the Portal log, and Undo brings it back for 10 minutes.`}
          action="Delete"
          danger
          busy={removing}
          onCancel={() => setDeleting(null)}
          onConfirm={() => void removeDec(deleting.id)}
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={
            toast.undoDec
              ? { label: 'Undo', onClick: () => void restoreDec(toast.undoDec!) }
              : undefined
          }
        />
      )}
    </div>
  );
}

function CodeCountdown({ left }: { left: number }) {
  return (
    <div className="cdown">
      <div className="cd-bar">
        <span style={{ width: `${Math.round((left / codeWaitSeconds) * 100)}%` }} />
      </div>
      <span>Retrieving the 2FA code, {left} seconds left</span>
    </div>
  );
}

/** The Admin's view of a failed attempt: its reference and the last page the bot saw. */
function AdminFailures({
  view,
  carrier,
  onShot,
}: {
  view: FetchView;
  carrier: string;
  onShot: (id: string, at: string) => void;
}) {
  const f = view.failures[0];
  if (!f) return null;
  return (
    <div className="det2-b">
      <div className="att">
        <span className="mono">{stampTime(f.at)}</span>
        <span>
          {failureReason(f.errorKind, carrier)}
          {f.errorRef && ` (reference ${f.errorRef})`}
        </span>
      </div>
      <AdminShot view={view} onShot={onShot} />
    </div>
  );
}

function AdminShot({
  view,
  onShot,
}: {
  view: FetchView;
  onShot: (id: string, at: string) => void;
}) {
  const f = view.failures.find((x) => x.screenshotId);
  if (!f?.screenshotId) return null;
  return (
    <ShotButton
      shot={{ screenshotId: f.screenshotId, at: f.at }}
      host={view.carrier?.siteUrl}
      onShot={onShot}
    />
  );
}

/** The screenshot of the last page the bot saw, as a small window picture (the Admin only). */
function ShotButton({
  shot,
  host,
  onShot,
}: {
  shot: { screenshotId: string; at: string };
  host: string | undefined;
  onShot: (id: string, at: string) => void;
}) {
  return (
    <div className="row g12" style={{ alignItems: 'flex-start' }}>
      <button
        type="button"
        className="shot"
        aria-label="Open screenshot of the last page the bot saw"
        onClick={() => onShot(shot.screenshotId, shot.at)}
      >
        <span className="shot-bar">
          <i />
          <i />
          <i />
          <b>{host ? new URL(host).hostname : 'Carrier site'}</b>
        </span>
        <span className="shot-body">
          <span className="shot-t">Screenshot</span>
          <span className="shot-l" />
          <span className="shot-l s" />
        </span>
      </button>
      <span className="t12 muted lh">
        Last page the bot saw, {stampTime(shot.at)}. Admin only. Click to enlarge.
      </span>
    </div>
  );
}

/** What was read from the dec page (spec C10): a spinner while it is read, then the values. */
function ReadingBlock({
  reading,
  onCheck,
}: {
  reading: NonNullable<FetchView['reading']>;
  onCheck?: (field: ReadingFixField) => void;
}) {
  if (readingOpen(reading)) {
    return (
      <span className="readline">
        <span className="spin" />
        {readingLine(reading)}
      </span>
    );
  }
  if (reading.status === 'failed') {
    return (
      <span className="okline amber">
        <AlertTriangleIcon className="ico s14" />
        <span>{readingLine(reading)}</span>
      </span>
    );
  }
  const checks = reading.checks;
  return (
    <>
      {otherInsured(reading) && (
        <div className="fsx red">
          <div className="fsx-h">
            <AlertCircleIcon />
            <span>
              This dec page may be for a different insured. Check it, and use Replace if it is
              wrong.
            </span>
          </div>
        </div>
      )}
      <span className="t12 text2">{readingLine(reading)}</span>
      {checks.length > 0 && (
        <div className="fsx amber">
          <div className="fsx-h">
            <AlertTriangleIcon />
            <span>{checksHeadline(checks.length)} with the PDF open beside it:</span>
          </div>
          <ul className="m0 t13 text2">
            {checks.map((c) => (
              <li key={c.field}>
                <span className="row g8 wrap">
                  {checkText(c)}
                  {isFixField(c.field) && reading.canFix && onCheck && (
                    <button
                      type="button"
                      className="chk-tag"
                      aria-haspopup="dialog"
                      aria-label={`Check ${checkText(c)}`}
                      onClick={() => onCheck(c.field as ReadingFixField)}
                    >
                      Check
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
